import { usePrinters } from '@/components/printing';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ACTION_DEFS,
  AUTOMATION_FIELDS,
  AUTOMATION_TRIGGERS,
  CONDITION_OPS,
  DEFAULT_ROLES,
  RECIPIENT_LABELS,
  REPAIR_STATUS_OPTIONS,
  TEMPLATE_VARIABLES,
  TRIGGER_KEYS,
  type AutomationActionType,
  type AutomationEntity,
  type AutomationTrigger,
  type FieldDef,
  type TriggerParam,
} from '@serviceportal/shared';
import { History, ListOrdered, Pencil, Plus, Sparkles, Trash2, Workflow, X, Zap } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Tabs } from '@/components/tickets';
import { CustomerPicker } from '@/components/customer-picker';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/components/ui/confirm';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { countSteps, FlowCanvas, newId, type Step } from '@/components/flow-canvas';
import { Field, PageHeader } from '@/components/ui/form-field';
import { Alert, Badge, Card, CardContent, Input, Select, Skeleton, Switch, Textarea } from '@/components/ui/primitives';
import { EmptyRow, Pagination, Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { api, errorMessage } from '@/lib/api';
import type { AutomationRuleItem, AutomationRunItem, Paginated, TicketMeta } from '@/lib/types';
import { formatDateTime, formatRelative } from '@/lib/utils';

const KEY = ['automations'];
const onError = (e: unknown) => toast.error(errorMessage(e));

type Condition = { field: string; op: keyof typeof CONDITION_OPS; value: string | number | boolean | string[] };
type Action = { type: AutomationActionType } & Record<string, unknown>;

interface Form {
  name: string;
  description: string;
  active: boolean;
  trigger: AutomationTrigger;
  triggerParams: Record<string, number>;
  conditions: Condition[];
  actions: Action[];
  /** Ablauf statt einfacher Aktionen (null/undefiniert = einfache Regel) */
  flow?: Step[] | null;
}

const entityOf = (t: AutomationTrigger): AutomationEntity => AUTOMATION_TRIGGERS[t].entity;
const ENTITY_LABELS: Record<AutomationEntity, string> = {
  ticket: 'Tickets',
  repair: 'Reparaturen',
  monitoring_event: 'Monitoring',
  contract: 'Verträge',
  appointment: 'Termine',
};
const PRIORITY_OPTIONS = [
  { value: 'URGENT', label: 'Dringend' },
  { value: 'HIGH', label: 'Hoch' },
  { value: 'NORMAL', label: 'Normal' },
  { value: 'LOW', label: 'Niedrig' },
];

function defaultAction(type: AutomationActionType, meta?: TicketMeta): Action {
  switch (type) {
    case 'send_email':
      return { type, recipients: ['assignee'], subject: '', body: '' };
    case 'set_priority':
      return { type, priority: 'HIGH' };
    case 'set_ticket_status':
      return { type, statusId: meta?.statuses.find((s) => s.scope === 'TICKET')?.id ?? '' };
    case 'assign':
      return { type, userId: null, teamId: null };
    case 'add_note':
      return { type, text: '' };
    case 'create_ticket':
      return { type, areaId: meta?.areas[0]?.id ?? '', priority: 'HIGH', subject: '' };
    case 'set_repair_status':
      return { type, status: 'IN_REPAIR' };
    case 'release_billing':
      return { type, note: '' };
    case 'print_document':
      return { type, document: 'label', printerId: null, copies: null };
    case 'invite_portal':
      return { type, role: 'auto' };
    case 'set_customer':
      return { type, customerId: '', contactId: null };
    case 'set_area':
      return { type, areaId: meta?.areas[0]?.id ?? '', categoryId: null };
    case 'print_ticket':
      return { type, what: 'ticket', printerId: null, copies: null };
    case 'notify_teams':
      return { type, channelId: '', title: '{{ticket.number}}: {{ticket.subject}}', text: '' };
    case 'notify_whatsapp':
      return { type, recipients: ['assignee'], text: 'Neues Ticket {{ticket.number}}: {{ticket.subject}}' };
    default:
      return { type };
  }
}

const paramsOf = (t: AutomationTrigger): TriggerParam[] => {
  const def = AUTOMATION_TRIGGERS[t] as { params?: TriggerParam[] };
  return def.params ?? [];
};

function defaultParams(t: AutomationTrigger) {
  return Object.fromEntries(paramsOf(t).map((p) => [p.key, p.default]));
}

/** Vorlagen für die Beispiele aus der Vorgabe. */
function templates(meta?: TicketMeta): { title: string; hint?: string; form: Form }[] {
  const it = meta?.areas.find((a) => a.key === 'it')?.id ?? meta?.areas[0]?.id ?? '';
  return [
    {
      title: 'Ablauf: Neues Ticket – dringend sofort melden, sonst nach 2 Stunden eskalieren',
      hint: 'Beispiel für einen Ablauf mit Verzweigung und Wartezeit – wie ein Callflow.',
      form: {
        name: 'Eingangsablauf neue Tickets',
        description: '',
        active: true,
        trigger: 'ticket.created',
        triggerParams: {},
        conditions: [],
        actions: [],
        flow: [
          {
            kind: 'branch',
            id: newId(),
            label: 'Dringlichkeit',
            branches: [
              {
                id: newId(),
                label: 'Dringend',
                match: 'all',
                conditions: [{ field: 'priority', op: 'eq', value: 'URGENT' }],
                steps: [
                  { kind: 'action', id: newId(), action: { type: 'send_email', recipients: ['role:service_lead'], subject: 'Dringendes Ticket {{ticket.number}}', body: '{{ticket.subject}}\nKunde: {{customer.name}}' } },
                  { kind: 'stop', id: newId(), label: 'Serviceleitung informiert' },
                ],
              },
            ],
            otherwise: [],
          },
          { kind: 'wait', id: newId(), label: 'Zwei Stunden', minutes: 120 },
          {
            kind: 'branch',
            id: newId(),
            label: 'Noch offen?',
            branches: [
              {
                id: newId(),
                label: 'Nicht zugewiesen',
                match: 'all',
                conditions: [{ field: 'assigned', op: 'eq', value: false }],
                steps: [
                  { kind: 'action', id: newId(), action: { type: 'set_priority', priority: 'HIGH' } },
                  { kind: 'action', id: newId(), action: { type: 'send_email', recipients: ['role:service_lead'], subject: 'Seit 2 Stunden nicht zugewiesen: {{ticket.number}}', body: '{{ticket.subject}}\nKunde: {{customer.name}}' } },
                ],
              },
            ],
            otherwise: [],
          },
        ],
      },
    },
    {
      title: 'Reparatur abholbereit → Kunden-E-Mail',
      hint: 'Das Portal sendet bei „Abholbereit“ bereits eine Standardnachricht. Diese Regel ergänzt einen eigenen Text.',
      form: {
        name: 'Abholbereit: Kunde informieren',
        description: '',
        active: true,
        trigger: 'repair.status_changed',
        triggerParams: {},
        conditions: [{ field: 'status', op: 'eq', value: 'READY_FOR_PICKUP' }],
        actions: [
          { type: 'send_email', recipients: ['customer'], subject: 'Ihr Gerät ist abholbereit ({{repair.number}})', body: 'Ihr Gerät {{repair.device}} ist fertig und kann abgeholt werden.\n\nUnsere Öffnungszeiten: Mo–Fr 9–18 Uhr.' },
        ],
      },
    },
    {
      title: 'Ticket 48 Stunden unbearbeitet → Serviceleitung',
      form: {
        name: 'Unbearbeitete Tickets eskalieren',
        description: '',
        active: true,
        trigger: 'ticket.idle',
        triggerParams: { hours: 48 },
        conditions: [],
        actions: [
          { type: 'send_email', recipients: ['role:service_lead'], subject: 'Seit 48 Stunden unbearbeitet: {{ticket.number}}', body: '{{ticket.subject}}\nKunde: {{customer.name}}\nBereich: {{ticket.area}}' },
          { type: 'add_note', text: 'Serviceleitung informiert: seit 48 Stunden keine Bearbeitung.' },
        ],
      },
    },
    {
      title: 'Kritisches Zabbix-/NetDoc-Problem → Ticket mit hoher Priorität',
      form: {
        name: 'Kritische Monitoring-Probleme',
        description: '',
        active: true,
        trigger: 'monitoring.problem',
        triggerParams: {},
        conditions: [
          { field: 'severity', op: 'gte', value: '4' },
          { field: 'hasTicket', op: 'eq', value: false },
        ],
        actions: [{ type: 'create_ticket', areaId: it, priority: 'HIGH', subject: '' }],
      },
    },
    {
      title: 'Wartungsvertrag abgelaufen → IT-Ticketberechtigung entziehen',
      hint: 'Berechtigungen aus Verträgen enden automatisch mit dem Vertrag. Die Regel entzieht zusätzlich manuell erteilte Bereichsfreigaben und informiert den Verantwortlichen.',
      form: {
        name: 'Vertragsende: Freigaben entziehen',
        description: '',
        active: true,
        trigger: 'contract.expired',
        triggerParams: {},
        conditions: [],
        actions: [
          { type: 'revoke_area_grants' },
          { type: 'send_email', recipients: ['owner'], subject: 'Vertrag {{contract.number}} abgelaufen', body: '{{contract.title}} ({{customer.name}}) ist abgelaufen. Manuelle Bereichsfreigaben wurden entzogen.' },
        ],
      },
    },
    {
      title: 'Kostenvoranschlag freigegeben → Reparatur fortsetzen',
      form: {
        name: 'Nach Freigabe in Reparatur',
        description: '',
        active: true,
        trigger: 'repair.quote_approved',
        triggerParams: {},
        conditions: [],
        actions: [{ type: 'set_repair_status', status: 'IN_REPAIR' }],
      },
    },
    {
      title: 'Kundenportal: Zugang bei Wartungsvertrag oder AVV',
      hint: 'Sobald ein Vertrag aktiv wird, erhält der Ansprechpartner des Vertrags (sonst der Hauptansprechpartner) eine Einladung. Mit der Bedingung „Art“ auf nur Wartungsverträge oder nur AVV einschränkbar.',
      form: { name: 'Portalzugang für Vertragskunden', description: '', active: true, trigger: 'contract.activated', triggerParams: {}, conditions: [], actions: [{ type: 'invite_portal', role: 'auto' }] },
    },
    {
      title: 'Kundenportal: Zugang bei Reparaturannahme',
      hint: 'Reparaturkunden verfolgen den Stand im Portal und geben Kostenvoranschläge frei.',
      form: { name: 'Portalzugang für Reparaturkunden', description: '', active: true, trigger: 'repair.created', triggerParams: {}, conditions: [], actions: [{ type: 'invite_portal', role: 'auto' }] },
    },
    {
      title: 'Kundenportal: Zugang für jeden Kunden mit Ticket',
      form: { name: 'Portalzugang für Ticketkunden', description: '', active: true, trigger: 'ticket.created', triggerParams: {}, conditions: [], actions: [{ type: 'invite_portal', role: 'auto' }] },
    },
    {
      title: 'E-Mail: Betreff/Absender → Kunde und Bearbeiter zuordnen',
      hint: 'Beispiel: Mails von @lieferant.de mit „Bestellung“ im Betreff gehen an einen festen Mitarbeiter. Kunde und Bearbeiter im Editor auswählen.',
      form: {
        name: 'E-Mail zuordnen',
        description: '',
        active: true,
        trigger: 'email.received',
        triggerParams: {},
        conditions: [
          { field: 'isReply', op: 'eq', value: false },
          { field: 'fromDomain', op: 'eq', value: 'lieferant.de' },
          { field: 'subject', op: 'contains', value: 'Bestellung' },
        ],
        actions: [{ type: 'assign', userId: null, teamId: null }],
      },
    },
    {
      title: 'E-Mail mit PDF-Anhang → Anhänge drucken',
      hint: 'Druckt die PDF-Anhänge eingehender Mails (z. B. Aufträge) auf den Standarddrucker „Ticket“.',
      form: {
        name: 'Mail-Anhänge drucken',
        description: '',
        active: true,
        trigger: 'email.received',
        triggerParams: {},
        conditions: [{ field: 'hasAttachments', op: 'eq', value: true }],
        actions: [{ type: 'print_ticket', what: 'attachments', printerId: null, copies: null }],
      },
    },
    {
      title: 'Gerät angenommen → Etikett und Annahmeschein drucken',
      hint: 'Druckt auf die Standarddrucker der Belege (Administration → Drucker).',
      form: {
        name: 'Annahme drucken',
        description: '',
        active: true,
        trigger: 'repair.created',
        triggerParams: {},
        conditions: [],
        actions: [
          { type: 'print_document', document: 'label', printerId: null, copies: null },
          { type: 'print_document', document: 'intake', printerId: null, copies: null },
        ],
      },
    },
    {
      title: 'Ticket gelöst → Bereit zur Rechnung',
      hint: 'Gelöste Tickets mit Kunde erscheinen in der Abrechnung zur Übernahme in Powerbird. Reparaturen werden bei „abholbereit“ automatisch freigegeben.',
      form: {
        name: 'Gelöste Tickets abrechnen',
        description: '',
        active: true,
        trigger: 'ticket.status_changed',
        triggerParams: {},
        conditions: [{ field: 'statusCategory', op: 'eq', value: 'RESOLVED' }],
        actions: [{ type: 'release_billing', note: '' }],
      },
    },
    {
      title: 'Termin erstellt → Outlook-Kalender aktualisieren',
      hint: 'Mit Microsoft 365 überträgt das Portal Termine ohnehin sofort nach Outlook. Die Regel stößt einen erneuten Abgleich an, z. B. nach einem Fehler.',
      form: { name: 'Termine nach Outlook', description: '', active: true, trigger: 'appointment.created', triggerParams: {}, conditions: [], actions: [{ type: 'sync_outlook' }] },
    },
    {
      title: 'SLA-Reaktionszeit überschritten → Priorität hoch und Serviceleitung',
      form: {
        name: 'SLA-Verletzung eskalieren',
        description: '',
        active: true,
        trigger: 'ticket.sla_response_breached',
        triggerParams: {},
        conditions: [],
        actions: [
          { type: 'set_priority', priority: 'HIGH' },
          { type: 'send_email', recipients: ['role:service_lead', 'assignee'], subject: 'SLA verletzt: {{ticket.number}}', body: 'Die Reaktionszeit für „{{ticket.subject}}“ ({{customer.name}}) wurde überschritten.' },
        ],
      },
    },
  ];
}

/** Kunde per Suche wählen; gespeichert wird nur die ID */
function CustomerIdPicker({ value, onChange }: { value: string; onChange: (id: string) => void }) {
  const { data } = useQuery({
    queryKey: ['customers', value],
    queryFn: () => api<{ id: string; type: string; companyName: string | null; firstName: string | null; lastName: string | null }>(`/customers/${value}`),
    enabled: Boolean(value),
  });
  const name = data ? (data.type === 'BUSINESS' && data.companyName ? data.companyName : [data.firstName, data.lastName].filter(Boolean).join(' ') || data.companyName || '') : '…';
  return <CustomerPicker value={value ? { id: value, name } : null} onChange={(c) => onChange(c?.id ?? '')} />;
}

function ValueInput({ def, value, onChange, meta }: { def: FieldDef; value: Condition['value']; onChange: (v: Condition['value']) => void; meta?: TicketMeta }) {
  if (def.type === 'customer') return <CustomerIdPicker value={String(value ?? '')} onChange={onChange} />;
  if (def.type === 'boolean')
    return (
      <Select value={String(value)} onChange={(e) => onChange(e.target.value === 'true')} aria-label="Wert">
        <option value="true">ja</option>
        <option value="false">nein</option>
      </Select>
    );
  if (def.type === 'enum' || def.type === 'area') {
    const options = def.type === 'area' ? (meta?.areas ?? []).map((a) => ({ value: a.id, label: a.name })) : (def.options ?? []);
    return (
      <Select value={String(value)} onChange={(e) => onChange(e.target.value)} aria-label="Wert">
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </Select>
    );
  }
  return <Input value={String(value)} onChange={(e) => onChange(e.target.value)} aria-label="Wert" />;
}

function RecipientsInput({ value, onChange, entity }: { value: string[]; onChange: (v: string[]) => void; entity: AutomationEntity }) {
  const [email, setEmail] = useState('');
  const base = ['customer', 'assignee', ...(entity === 'ticket' || entity === 'monitoring_event' ? ['team'] : []), ...(entity === 'contract' ? ['owner'] : []), ...(entity === 'appointment' ? ['technicians'] : [])];
  const label = (r: string) =>
    RECIPIENT_LABELS[r] ?? (r.startsWith('role:') ? `Rolle: ${DEFAULT_ROLES.find((x) => x.key === r.slice(5))?.name ?? r.slice(5)}` : r.startsWith('email:') ? r.slice(6) : r);
  const add = (r: string) => !value.includes(r) && onChange([...value, r]);
  return (
    <div className="grid gap-2">
      <div className="flex flex-wrap gap-1.5">
        {value.map((r) => (
          <Badge key={r} variant="secondary" className="gap-1">
            {label(r)}
            <button type="button" onClick={() => onChange(value.filter((x) => x !== r))} aria-label={`${label(r)} entfernen`}>
              <X className="size-3" />
            </button>
          </Badge>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        <Select className="w-auto" value="" onChange={(e) => e.target.value && add(e.target.value)} aria-label="Empfänger hinzufügen">
          <option value="">+ Empfänger</option>
          {base.map((r) => (
            <option key={r} value={r}>
              {RECIPIENT_LABELS[r]}
            </option>
          ))}
          {DEFAULT_ROLES.filter((r) => r.audience === 'INTERNAL' && r.key !== 'platform_admin').map((r) => (
            <option key={r.key} value={`role:${r.key}`}>
              Rolle: {r.name}
            </option>
          ))}
        </Select>
        <Input className="w-56" type="email" placeholder="weitere@adresse.de" value={email} onChange={(e) => setEmail(e.target.value)} aria-label="E-Mail-Adresse" />
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)}
          onClick={() => {
            add(`email:${email.trim()}`);
            setEmail('');
          }}
        >
          Hinzufügen
        </Button>
      </div>
    </div>
  );
}

const PRINT_DOCS = [
  { value: 'intake', label: 'Annahmeschein', entity: 'repair' },
  { value: 'label', label: 'Geräteetikett', entity: 'repair' },
  { value: 'workshop', label: 'Werkstattbegleitschein', entity: 'repair' },
  { value: 'quote', label: 'Kostenvoranschlag', entity: 'repair' },
  { value: 'report', label: 'Reparaturbericht', entity: 'repair' },
  { value: 'pickup', label: 'Abholbeleg', entity: 'repair' },
  { value: 'contract', label: 'Vertrag', entity: 'contract' },
] as const;

function PrintActionEditor({ action, set, entity }: { action: Action; set: (k: string, v: unknown) => void; entity: AutomationEntity }) {
  const { data } = usePrinters();
  return (
    <div className="grid gap-2 sm:grid-cols-3">
      <Select value={String(action.document)} onChange={(e) => set('document', e.target.value)} aria-label="Beleg">
        {PRINT_DOCS.filter((d) => d.entity === entity).map((d) => (
          <option key={d.value} value={d.value}>
            {d.label}
          </option>
        ))}
      </Select>
      <Select value={String(action.printerId ?? '')} onChange={(e) => set('printerId', e.target.value || null)} aria-label="Drucker">
        <option value="">Standarddrucker des Belegs</option>
        {data?.items.filter((p) => p.active).map((p) => (
          <option key={p.id} value={p.id}>
            {p.name}
          </option>
        ))}
      </Select>
      <Input type="number" min={1} max={20} value={action.copies ? String(action.copies) : ''} onChange={(e) => set('copies', e.target.value ? Number(e.target.value) : null)} placeholder="Exemplare (Standard)" aria-label="Exemplare" />
    </div>
  );
}

function TeamsActionEditor({ action, set, entity }: { action: Action; set: (k: string, v: unknown) => void; entity: AutomationEntity }) {
  const { data } = useQuery({ queryKey: ['teams-channels'], queryFn: () => api<{ items: { id: string; name: string; active: boolean }[] }>('/integrations/teams') });
  return (
    <div className="grid gap-2">
      <Select value={String(action.channelId ?? '')} onChange={(e) => set('channelId', e.target.value)} aria-label="Teams-Kanal">
        <option value="">– Kanal wählen –</option>
        {data?.items.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
            {c.active ? '' : ' (aus)'}
          </option>
        ))}
      </Select>
      {data?.items.length === 0 && (
        <p className="text-xs text-muted-foreground">
          Noch kein Kanal – unter{' '}
          <Link to="/admin/integrations/teams" className="text-primary underline">
            Integrationen → Microsoft Teams
          </Link>{' '}
          einrichten.
        </p>
      )}
      <Input value={String(action.title ?? '')} onChange={(e) => set('title', e.target.value)} placeholder="Überschrift" aria-label="Überschrift" />
      <Textarea rows={2} value={String(action.text ?? '')} onChange={(e) => set('text', e.target.value)} placeholder="Text (optional)" aria-label="Text" />
      <p className="text-xs text-muted-foreground">
        Platzhalter: {TEMPLATE_VARIABLES[entity].map((v) => <code key={v} className="mx-0.5">{`{{${v}}}`}</code>)} – die Karte enthält einen Link zum Vorgang.
      </p>
    </div>
  );
}

function WhatsAppActionEditor({ action, set, entity }: { action: Action; set: (k: string, v: unknown) => void; entity: AutomationEntity }) {
  const [phone, setPhone] = useState('');
  const value = (action.recipients as string[]) ?? [];
  const base = ['assignee', ...(entity === 'ticket' || entity === 'monitoring_event' ? ['team'] : []), ...(entity === 'contract' ? ['owner'] : []), ...(entity === 'appointment' ? ['technicians'] : [])];
  const label = (r: string) =>
    RECIPIENT_LABELS[r] ?? (r.startsWith('role:') ? `Rolle: ${DEFAULT_ROLES.find((x) => x.key === r.slice(5))?.name ?? r.slice(5)}` : r.startsWith('phone:') ? r.slice(6) : r);
  const add = (r: string) => !value.includes(r) && set('recipients', [...value, r]);
  return (
    <div className="grid gap-2">
      <div className="flex flex-wrap gap-1.5">
        {value.map((r) => (
          <Badge key={r} variant="secondary" className="gap-1">
            {label(r)}
            <button type="button" onClick={() => set('recipients', value.filter((x) => x !== r))} aria-label={`${label(r)} entfernen`}>
              <X className="size-3" />
            </button>
          </Badge>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        <Select value="" onChange={(e) => e.target.value && add(e.target.value)} aria-label="Empfänger hinzufügen" className="w-auto">
          <option value="">+ Mitarbeitende</option>
          {base.map((r) => (
            <option key={r} value={r}>
              {RECIPIENT_LABELS[r]}
            </option>
          ))}
          {DEFAULT_ROLES.filter((r) => r.audience === 'INTERNAL').map((r) => (
            <option key={r.key} value={`role:${r.key}`}>
              Rolle: {r.name}
            </option>
          ))}
        </Select>
        <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+49 151 …" aria-label="Feste Nummer" className="w-44" />
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!/^\+?[0-9 ]{8,20}$/.test(phone.trim())}
          onClick={() => {
            add(`phone:${phone.trim()}`);
            setPhone('');
          }}
        >
          Nummer hinzufügen
        </Button>
      </div>
      <Textarea rows={2} value={String(action.text ?? '')} onChange={(e) => set('text', e.target.value)} placeholder="Nachricht" aria-label="Nachricht" />
      <p className="text-xs text-muted-foreground">
        Mitarbeitende erhalten die Nachricht an die Telefonnummer aus ihrem Benutzerprofil. Für Zustellung zu jeder Zeit unter Integrationen → WhatsApp eine Vorlage für interne Benachrichtigungen eintragen. Platzhalter:{' '}
        {TEMPLATE_VARIABLES[entity].map((v) => <code key={v} className="mx-0.5">{`{{${v}}}`}</code>)}
      </p>
    </div>
  );
}

function PrintTicketEditor({ action, set }: { action: Action; set: (k: string, v: unknown) => void }) {
  const { data } = usePrinters();
  return (
    <div className="grid gap-2 sm:grid-cols-3">
      <Select value={String(action.what ?? 'ticket')} onChange={(e) => set('what', e.target.value)} aria-label="Was drucken">
        <option value="ticket">Ticket (PDF)</option>
        <option value="attachments">PDF-Anhänge der E-Mail bzw. des Tickets</option>
        <option value="both">Ticket und PDF-Anhänge</option>
      </Select>
      <Select value={String(action.printerId ?? '')} onChange={(e) => set('printerId', e.target.value || null)} aria-label="Drucker">
        <option value="">Standarddrucker „Ticket“</option>
        {data?.items.filter((p) => p.active).map((p) => (
          <option key={p.id} value={p.id}>
            {p.name}
          </option>
        ))}
      </Select>
      <Input type="number" min={1} max={20} value={action.copies ? String(action.copies) : ''} onChange={(e) => set('copies', e.target.value ? Number(e.target.value) : null)} placeholder="Exemplare (Standard)" aria-label="Exemplare" />
    </div>
  );
}

function ActionEditor({ action, onChange, entity, meta }: { action: Action; onChange: (a: Action) => void; entity: AutomationEntity; meta?: TicketMeta }) {
  const set = (k: string, v: unknown) => onChange({ ...action, [k]: v });
  switch (action.type) {
    case 'send_email':
      return (
        <div className="grid gap-3">
          <RecipientsInput value={(action.recipients as string[]) ?? []} onChange={(v) => set('recipients', v)} entity={entity} />
          <Input placeholder="Betreff" value={String(action.subject ?? '')} onChange={(e) => set('subject', e.target.value)} aria-label="Betreff" />
          <Textarea rows={3} placeholder="Text (Leerzeile = neuer Absatz)" value={String(action.body ?? '')} onChange={(e) => set('body', e.target.value)} aria-label="Text" />
          <p className="text-xs text-muted-foreground">
            Platzhalter: {TEMPLATE_VARIABLES[entity].map((v) => <code key={v} className="mx-0.5">{`{{${v}}}`}</code>)}
          </p>
        </div>
      );
    case 'set_priority':
      return (
        <Select value={String(action.priority)} onChange={(e) => set('priority', e.target.value)} aria-label="Priorität">
          {PRIORITY_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Select>
      );
    case 'set_ticket_status':
      return (
        <Select value={String(action.statusId)} onChange={(e) => set('statusId', e.target.value)} aria-label="Status">
          {meta?.statuses.filter((s) => s.scope === 'TICKET').map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
      );
    case 'assign':
      return (
        <div className="grid gap-2 sm:grid-cols-2">
          <Select value={String(action.userId ?? '')} onChange={(e) => set('userId', e.target.value || null)} aria-label="Bearbeiter">
            <option value="">– Bearbeiter unverändert –</option>
            {meta?.agents.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </Select>
          <Select value={String(action.teamId ?? '')} onChange={(e) => set('teamId', e.target.value || null)} aria-label="Team">
            <option value="">– Team unverändert –</option>
            {meta?.teams.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </Select>
        </div>
      );
    case 'print_document':
      return <PrintActionEditor action={action} set={set} entity={entity} />;
    case 'set_customer':
      return (
        <div className="grid gap-1">
          <CustomerIdPicker value={String(action.customerId ?? '')} onChange={(id) => set('customerId', id)} />
          <p className="text-xs text-muted-foreground">Der Ansprechpartner wird entfernt, wenn er nicht zum neuen Kunden gehört.</p>
        </div>
      );
    case 'set_area':
      return (
        <div className="grid gap-2 sm:grid-cols-2">
          <Select value={String(action.areaId)} onChange={(e) => onChange({ ...action, areaId: e.target.value, categoryId: null })} aria-label="Bereich">
            {meta?.areas.filter((a) => a.active).map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </Select>
          <Select value={String(action.categoryId ?? '')} onChange={(e) => set('categoryId', e.target.value || null)} aria-label="Kategorie">
            <option value="">– Kategorie unverändert –</option>
            {meta?.categories.filter((c) => !c.areaId || c.areaId === action.areaId).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        </div>
      );
    case 'print_ticket':
      return <PrintTicketEditor action={action} set={set} />;
    case 'notify_teams':
      return <TeamsActionEditor action={action} set={set} entity={entity} />;
    case 'notify_whatsapp':
      return <WhatsAppActionEditor action={action} set={set} entity={entity} />;
    case 'invite_portal':
      return (
        <div className="grid gap-1">
          <Select value={String(action.role ?? 'auto')} onChange={(e) => set('role', e.target.value)} aria-label="Portalrolle">
            <option value="auto">Rolle automatisch (Firma: Administrator bzw. Mitarbeiter, Privatkunde)</option>
            <option value="customer_admin">Kundenadministrator</option>
            <option value="customer_employee">Kundenmitarbeiter</option>
            <option value="private_customer">Privatkunde</option>
          </Select>
          <p className="text-xs text-muted-foreground">Lädt den Ansprechpartner des Vorgangs (sonst den Hauptansprechpartner) per E-Mail ein. Bestehende oder gesperrte Zugänge bleiben unverändert.</p>
        </div>
      );
    case 'release_billing':
      return <Input value={String(action.note ?? '')} onChange={(e) => set('note', e.target.value)} placeholder="Hinweis an die Buchhaltung (optional, Platzhalter möglich)" aria-label="Hinweis" />;
    case 'add_note':
      return <Textarea rows={2} value={String(action.text ?? '')} onChange={(e) => set('text', e.target.value)} placeholder="Interne Notiz" aria-label="Notiz" />;
    case 'create_ticket':
      return (
        <div className="grid gap-2 sm:grid-cols-3">
          <Select value={String(action.areaId)} onChange={(e) => set('areaId', e.target.value)} aria-label="Bereich">
            {meta?.areas.filter((a) => a.active).map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </Select>
          <Select value={String(action.priority)} onChange={(e) => set('priority', e.target.value)} aria-label="Priorität">
            {PRIORITY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                Priorität {o.label}
              </option>
            ))}
          </Select>
          <Input value={String(action.subject ?? '')} onChange={(e) => set('subject', e.target.value)} placeholder="Betreff (optional)" aria-label="Betreff" />
        </div>
      );
    case 'set_repair_status':
      return (
        <Select value={String(action.status)} onChange={(e) => set('status', e.target.value)} aria-label="Reparaturstatus">
          {REPAIR_STATUS_OPTIONS.filter((o) => ['IN_REPAIR', 'WAITING_PARTS', 'TESTING', 'DIAGNOSIS'].includes(o.value)).map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Select>
      );
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Ablauf-Editor (Flow): Schritte, Verzweigungen „Wenn … / Sonst“, Warten, Ende
// ---------------------------------------------------------------------------

/** Bedingungszeilen (Feld – Vergleich – Wert), gemeinsam für Regel und Zweige. */
function ConditionRows({ conditions, onChange, entity, meta }: { conditions: Condition[]; onChange: (c: Condition[]) => void; entity: AutomationEntity; meta?: TicketMeta }) {
  const fields = AUTOMATION_FIELDS[entity];
  const firstField = Object.keys(fields)[0]!;
  const defaultValue = (field: string): Condition['value'] => {
    const d = fields[field]!;
    return d.type === 'boolean' ? true : d.type === 'area' ? (meta?.areas[0]?.id ?? '') : d.type === 'customer' ? '' : (d.options?.[0]?.value ?? '');
  };
  return (
    <div className="grid gap-2">
      {conditions.map((c, i) => {
        const def = fields[c.field] ?? fields[firstField]!;
        const ops = (Object.keys(CONDITION_OPS) as (keyof typeof CONDITION_OPS)[]).filter((op) =>
          def.type === 'boolean'
            ? op === 'eq'
            : def.type === 'text'
              ? ['eq', 'neq', 'contains', 'not_contains', 'starts_with', 'ends_with'].includes(op)
              : op === 'eq' || op === 'neq' || (c.field === 'severity' && (op === 'gte' || op === 'lte')),
        );
        const update = (patch: Partial<Condition>) => onChange(conditions.map((x, j) => (j === i ? { ...x, ...patch } : x)));
        return (
          <div key={i} className="grid gap-2 sm:grid-cols-[1fr_10rem_1fr_auto]">
            <Select value={c.field} onChange={(e) => update({ field: e.target.value, op: 'eq', value: defaultValue(e.target.value) })} aria-label="Feld">
              {Object.entries(fields).map(([k, f]) => (
                <option key={k} value={k}>
                  {f.label}
                </option>
              ))}
            </Select>
            <Select value={c.op} onChange={(e) => update({ op: e.target.value as Condition['op'] })} aria-label="Vergleich">
              {ops.map((op) => (
                <option key={op} value={op}>
                  {CONDITION_OPS[op]}
                </option>
              ))}
            </Select>
            <ValueInput def={def} value={c.value} onChange={(v) => update({ value: v })} meta={meta} />
            <Button type="button" variant="ghost" size="icon" className="size-9" onClick={() => onChange(conditions.filter((_, j) => j !== i))} aria-label="Bedingung entfernen">
              <Trash2 />
            </Button>
          </div>
        );
      })}
      <div>
        <Button type="button" size="sm" variant="outline" onClick={() => onChange([...conditions, { field: firstField, op: 'eq', value: defaultValue(firstField) }])}>
          <Plus /> Bedingung
        </Button>
      </div>
    </div>
  );
}

function firstAction(entity: AutomationEntity, meta?: TicketMeta) {
  return defaultAction((Object.keys(ACTION_DEFS) as AutomationActionType[]).find((t) => ACTION_DEFS[t].entities.includes(entity))!, meta);
}

/** Kurzbeschreibung einer Aktion für die Karte im Ablauf. */
function describeAction(a: Action, meta?: TicketMeta): { title: string; lines: string[] } {
  const title = ACTION_DEFS[a.type]?.label ?? a.type;
  const name = (list: { id: string; name: string }[] | undefined, id: unknown) => list?.find((x) => x.id === id)?.name ?? '–';
  const who = (r: string) => RECIPIENT_LABELS[r] ?? (r.startsWith('role:') ? `Rolle ${DEFAULT_ROLES.find((x) => x.key === r.slice(5))?.name ?? r.slice(5)}` : r.replace(/^(email|phone):/, ''));
  switch (a.type) {
    case 'send_email':
      return { title, lines: [`An ist ${((a.recipients as string[]) ?? []).map(who).join(', ') || '–'}`, `Betreff ist ${String(a.subject ?? '')}`] };
    case 'set_priority':
      return { title, lines: [`Priorität ist ${PRIORITY_OPTIONS.find((o) => o.value === a.priority)?.label ?? a.priority}`] };
    case 'set_ticket_status':
      return { title, lines: [`Status ist ${name(meta?.statuses, a.statusId)}`] };
    case 'assign':
      return { title, lines: [`Bearbeiter ist ${a.userId ? name(meta?.agents, a.userId) : '–'}`, `Team ist ${a.teamId ? name(meta?.teams, a.teamId) : '–'}`] };
    case 'add_note':
      return { title, lines: [String(a.text ?? '')] };
    case 'create_ticket':
      return { title, lines: [`Bereich ist ${name(meta?.areas, a.areaId)}`, `Priorität ist ${PRIORITY_OPTIONS.find((o) => o.value === a.priority)?.label ?? a.priority}`] };
    case 'notify_teams':
      return { title, lines: [String(a.title ?? '')] };
    case 'notify_whatsapp':
      return { title, lines: [`An ist ${((a.recipients as string[]) ?? []).map(who).join(', ')}`, String(a.text ?? '')] };
    case 'set_area':
      return { title, lines: [`Bereich ist ${name(meta?.areas, a.areaId)}`] };
    default: {
      const lines = Object.entries(a)
        .filter(([k, v]) => k !== 'type' && (typeof v === 'string' || typeof v === 'number') && v !== '')
        .slice(0, 2)
        .map(([, v]) => String(v));
      return { title, lines };
    }
  }
}

/** Kurzbeschreibung einer Bedingung für die Zweigbeschriftung. */
function describeCondition(c: Condition, entity: AutomationEntity, meta?: TicketMeta) {
  const def = AUTOMATION_FIELDS[entity][c.field];
  const v = (x: unknown) =>
    def?.type === 'boolean' ? (x === true || x === 'true' ? 'ja' : 'nein') : def?.type === 'area' ? (meta?.areas.find((a) => a.id === x)?.name ?? String(x)) : (def?.options?.find((o) => o.value === String(x))?.label ?? String(x));
  const value = Array.isArray(c.value) ? c.value.map(v).join(', ') : v(c.value);
  return `${def?.label ?? c.field} ${CONDITION_OPS[c.op]} ${value}`;
}

function newStep(kind: Step['kind'], entity: AutomationEntity, meta?: TicketMeta): Step {
  const fields = AUTOMATION_FIELDS[entity];
  const f = Object.keys(fields)[0]!;
  const d = fields[f]!;
  const value: Condition['value'] = d.type === 'boolean' ? true : d.type === 'area' ? (meta?.areas[0]?.id ?? '') : (d.options?.[0]?.value ?? '');
  if (kind === 'action') return { kind, id: newId(), action: firstAction(entity, meta) };
  if (kind === 'wait') return { kind, id: newId(), minutes: 60 };
  if (kind === 'stop') return { kind, id: newId() };
  return { kind, id: newId(), branches: [{ id: newId(), label: 'Wenn', match: 'all', conditions: [{ field: f, op: 'eq', value }], steps: [] }], otherwise: [] };
}

// ---------------------------------------------------------------------------

function RuleDialog({ edit, onClose }: { edit: { id: string | null; form: Form } | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState<Form | null>(null);
  const { data: meta } = useQuery({ queryKey: ['tickets', 'meta'], queryFn: () => api<TicketMeta>('/tickets/meta') });
  useEffect(() => setForm(edit?.form ?? null), [edit]);
  const save = useMutation({
    mutationFn: () => {
      const fieldsOf = AUTOMATION_FIELDS[entityOf(form!.trigger)];
      const norm = (list: Condition[]) => list.map((c) => ({ ...c, value: fieldsOf[c.field]?.options && c.op !== 'in' && /^\d+$/.test(String(c.value)) && ['gte', 'lte'].includes(c.op) ? Number(c.value) : c.value }));
      const normSteps = (list: Step[]): Step[] =>
        list.map((st) => (st.kind === 'branch' ? { ...st, branches: st.branches.map((b) => ({ ...b, conditions: norm(b.conditions as Condition[]), steps: normSteps(b.steps) })), otherwise: normSteps(st.otherwise) } : st));
      const body = {
        ...form!,
        description: form!.description || null,
        conditions: norm(form!.conditions),
        actions: form!.flow ? [] : form!.actions,
        flow: form!.flow ? normSteps(form!.flow) : null,
      };
      return edit!.id ? api(`/automations/${edit!.id}`, { method: 'PUT', body }) : api('/automations', { method: 'POST', body });
    },
    onSuccess: () => {
      toast.success('Regel gespeichert');
      qc.invalidateQueries({ queryKey: KEY });
      onClose();
    },
    onError,
  });
  if (!edit || !form) return null;
  const entity = entityOf(form.trigger);
  const fields = AUTOMATION_FIELDS[entity];
  const params = paramsOf(form.trigger);
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => f && { ...f, [k]: v });
  const isFlow = Boolean(form.flow);
  const flowStats = form.flow ? countSteps(form.flow) : null;
  const flowOnlyActions = form.flow?.every((st) => st.kind === 'action') ?? true;

  const changeTrigger = (t: AutomationTrigger) =>
    setForm(
      (f) =>
        f && {
          ...f,
          trigger: t,
          triggerParams: defaultParams(t),
          conditions: entityOf(t) === entity ? f.conditions : [],
          actions: f.actions.filter((a) => ACTION_DEFS[a.type].entities.includes(entityOf(t))),
          // Anderer Datensatztyp: Felder und Aktionen des Ablaufs passen nicht mehr
          flow: f.flow && entityOf(t) !== entity ? [] : f.flow,
        },
    );
  const setMode = (flow: boolean) =>
    setForm(
      (f) =>
        f &&
        (flow
          ? { ...f, flow: f.flow ?? f.actions.map((a) => ({ kind: 'action' as const, id: newId(), action: a })) }
          : { ...f, actions: (f.flow ?? []).flatMap((st) => (st.kind === 'action' ? [st.action as Action] : [])), flow: null }),
    );

  const triggerSelect = (
    <Select value={form.trigger} onChange={(e) => changeTrigger(e.target.value as AutomationTrigger)} aria-label="Auslöser">
      {(Object.keys(ENTITY_LABELS) as AutomationEntity[]).map((ent) => (
        <optgroup key={ent} label={ENTITY_LABELS[ent]}>
          {TRIGGER_KEYS.filter((k) => AUTOMATION_TRIGGERS[k].entity === ent).map((k) => (
            <option key={k} value={k}>
              {AUTOMATION_TRIGGERS[k].label}
            </option>
          ))}
        </optgroup>
      ))}
    </Select>
  );
  const triggerParams = params.map((p) => (
    <label key={p.key} className="flex items-center gap-2 text-sm">
      <Input
        type="number"
        className="w-24"
        min={p.min}
        max={p.max}
        value={form.triggerParams[p.key] ?? p.default}
        onChange={(e) => set('triggerParams', { ...form.triggerParams, [p.key]: Number(e.target.value) })}
        aria-label={p.label}
      />
      <span className="whitespace-nowrap">{p.label}</span>
    </label>
  ));
  const modeSwitch = (compact: boolean) => (
    <div className={compact ? 'inline-flex rounded-md border p-0.5' : 'grid grid-cols-2 gap-2'} role="radiogroup" aria-label="Art der Regel">
      {(
        [
          { flow: false, icon: <ListOrdered className="size-4" />, title: 'Einfache Regel', hint: 'Aktionen der Reihe nach' },
          { flow: true, icon: <Workflow className="size-4" />, title: 'Ablauf', hint: 'Wenn/Sonst-Zweige, Warten, Ende' },
        ] as const
      ).map((m) => (
        <button
          key={String(m.flow)}
          type="button"
          role="radio"
          aria-checked={isFlow === m.flow}
          disabled={!m.flow && !flowOnlyActions}
          title={!m.flow && !flowOnlyActions ? 'Der Ablauf enthält Verzweigungen oder Wartezeiten – erst entfernen' : undefined}
          onClick={() => setMode(m.flow)}
          className={
            compact
              ? `flex items-center gap-1.5 rounded px-2.5 py-1.5 text-sm disabled:opacity-50 ${isFlow === m.flow ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`
              : `flex items-start gap-2 rounded-lg border p-3 text-left text-sm transition-colors disabled:opacity-50 ${isFlow === m.flow ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'hover:bg-muted'}`
          }
        >
          <span className={compact ? '' : 'mt-0.5 text-muted-foreground'}>{m.icon}</span>
          {compact ? (
            m.title
          ) : (
            <span>
              <span className="block font-medium">{m.title}</span>
              <span className="text-muted-foreground">{m.hint}</span>
            </span>
          )}
        </button>
      ))}
    </div>
  );
  const footer = (
    <DialogFooter>
      {isFlow && flowStats && (
        <p className="mr-auto self-center text-xs text-muted-foreground">
          {flowStats.total} Schritte · {flowStats.actions} Aktionen · {flowStats.branches} Verzweigungen · {flowStats.waits} Wartezeiten
        </p>
      )}
      <Button type="button" variant="outline" onClick={onClose}>
        Abbrechen
      </Button>
      <Button type="submit" loading={save.isPending} disabled={!form.name || (isFlow ? (flowStats?.actions ?? 0) === 0 : form.actions.length === 0)}>
        Speichern
      </Button>
    </DialogFooter>
  );
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    save.mutate();
  };

  // ------------------------------------------------------------- Ablauf: Vollbild mit Zeichenfläche
  if (isFlow) {
    return (
      <Dialog open onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="h-[calc(100dvh-2rem)] max-w-[1600px]">
          <form className="flex min-h-0 flex-1 flex-col" onSubmit={submit}>
            <DialogHeader className="pb-3">
              <DialogTitle>{edit.id ? 'Ablauf bearbeiten' : 'Neuer Ablauf'}</DialogTitle>
              <DialogDescription className="sr-only">Schritte mit Verzweigungen, Wartezeiten und Ende – wie ein Callflow.</DialogDescription>
            </DialogHeader>
            <div className="flex flex-wrap items-end gap-3 border-b px-6 pb-3">
              <Field label="Name" htmlFor="r-name" className="min-w-56 flex-1">
                <Input id="r-name" required value={form.name} onChange={(e) => set('name', e.target.value)} />
              </Field>
              <Field label="Start bei" htmlFor="r-trigger" className="min-w-64 flex-1">
                {triggerSelect}
              </Field>
              {triggerParams}
              <details className="group relative">
                <summary className="flex h-9 cursor-pointer list-none items-center gap-1.5 rounded-md border px-3 text-sm hover:bg-muted">
                  Bedingungen{form.conditions.length ? ` (${form.conditions.length})` : ''}
                </summary>
                <div className="absolute right-0 z-20 mt-1 grid w-[min(640px,calc(100vw-4rem))] gap-2 rounded-lg border bg-popover p-4 shadow-xl">
                  <p className="text-xs text-muted-foreground">Der Ablauf startet nur, wenn alle Bedingungen zutreffen.</p>
                  <ConditionRows conditions={form.conditions} onChange={(c) => set('conditions', c)} entity={entity} meta={meta} />
                </div>
              </details>
              <label className="flex h-9 items-center gap-2 text-sm">
                <Switch checked={form.active} onCheckedChange={(v) => set('active', v)} /> Aktiv
              </label>
              {modeSwitch(true)}
            </div>
            <div className="min-h-0 flex-1 p-3">
              <FlowCanvas
                steps={form.flow!}
                onChange={(f) => set('flow', f)}
                triggerLabel={AUTOMATION_TRIGGERS[form.trigger].label}
                newStep={(k) => newStep(k, entity, meta)}
                describeAction={(a) => describeAction(a as Action, meta)}
                describeCondition={(c) => describeCondition(c as Condition, entity, meta)}
                renderActionEditor={(a, change) => (
                  <>
                    <Select value={a.type} onChange={(e) => change(defaultAction(e.target.value as AutomationActionType, meta))} aria-label="Aktion">
                      {(Object.keys(ACTION_DEFS) as AutomationActionType[])
                        .filter((t) => ACTION_DEFS[t].entities.includes(entity))
                        .map((t) => (
                          <option key={t} value={t}>
                            {ACTION_DEFS[t].label}
                          </option>
                        ))}
                    </Select>
                    <ActionEditor action={a as Action} entity={entity} meta={meta} onChange={(na) => change(na)} />
                  </>
                )}
                renderConditions={(conds, change) => <ConditionRows conditions={conds as Condition[]} onChange={(c) => change(c)} entity={entity} meta={meta} />}
              />
            </div>
            {footer}
          </form>
        </DialogContent>
      </Dialog>
    );
  }

  // ------------------------------------------------------------- Einfache Regel
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent wide>
        <form className="flex min-h-0 flex-col" onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>{edit.id ? 'Regel bearbeiten' : 'Neue Automatisierung'}</DialogTitle>
            <DialogDescription>Wenn der Auslöser eintritt und alle Bedingungen zutreffen, werden die Aktionen der Reihe nach ausgeführt.</DialogDescription>
          </DialogHeader>
          <DialogBody className="grid gap-5">
            <div className="grid gap-4 sm:grid-cols-[1fr_auto]">
              <Field label="Name" htmlFor="r-name">
                <Input id="r-name" required value={form.name} onChange={(e) => set('name', e.target.value)} />
              </Field>
              <label className="flex items-center gap-2 self-end pb-2 text-sm">
                <Switch checked={form.active} onCheckedChange={(v) => set('active', v)} /> Aktiv
              </label>
            </div>

            <fieldset className="grid gap-3 rounded-lg border p-4">
              <legend className="px-1 text-sm font-medium">Wenn …</legend>
              {triggerSelect}
              {triggerParams}
            </fieldset>

            <fieldset className="grid gap-2 rounded-lg border p-4">
              <legend className="px-1 text-sm font-medium">… und (alle Bedingungen)</legend>
              {form.conditions.length === 0 && <p className="text-sm text-muted-foreground">Keine Bedingungen – die Regel gilt immer.</p>}
              <ConditionRows conditions={form.conditions} onChange={(c) => set('conditions', c)} entity={entity} meta={meta} />
            </fieldset>

            {modeSwitch(false)}

            <fieldset className="grid gap-3 rounded-lg border p-4">
              <legend className="px-1 text-sm font-medium">… dann</legend>
              {form.actions.map((a, i) => (
                <div key={i} className="grid gap-2 rounded-md border bg-muted/20 p-3">
                  <div className="flex items-center gap-2">
                    <Select
                      value={a.type}
                      onChange={(e) => set('actions', form.actions.map((x, j) => (j === i ? defaultAction(e.target.value as AutomationActionType, meta) : x)))}
                      aria-label="Aktion"
                    >
                      {(Object.keys(ACTION_DEFS) as AutomationActionType[])
                        .filter((t) => ACTION_DEFS[t].entities.includes(entity))
                        .map((t) => (
                          <option key={t} value={t}>
                            {ACTION_DEFS[t].label}
                          </option>
                        ))}
                    </Select>
                    <Button type="button" variant="ghost" size="icon" className="size-9 shrink-0" onClick={() => set('actions', form.actions.filter((_, j) => j !== i))} aria-label="Aktion entfernen">
                      <Trash2 />
                    </Button>
                  </div>
                  <ActionEditor action={a} entity={entity} meta={meta} onChange={(na) => set('actions', form.actions.map((x, j) => (j === i ? na : x)))} />
                </div>
              ))}
              <div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => set('actions', [...form.actions, defaultAction((Object.keys(ACTION_DEFS) as AutomationActionType[]).find((t) => ACTION_DEFS[t].entities.includes(entity))!, meta)])}
                >
                  <Plus /> Aktion
                </Button>
              </div>
            </fieldset>
          </DialogBody>
          {footer}
        </form>
      </DialogContent>
    </Dialog>
  );
}

const entityLink = (r: AutomationRunItem) =>
  r.entityType === 'ticket'
    ? `/tickets/${r.entityId}`
    : r.entityType === 'repair'
      ? `/repairs/${r.entityId}`
      : r.entityType === 'contract'
        ? `/contracts?id=${r.entityId}`
        : r.entityType === 'monitoring_event'
          ? '/monitoring'
          : '/calendar';

function RunsLog() {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  const params = new URLSearchParams({ page: String(page), pageSize: '25', ...(status && { status }) });
  const { data } = useQuery({ queryKey: [...KEY, 'runs', params.toString()], queryFn: () => api<Paginated<AutomationRunItem>>(`/automations/runs?${params}`), refetchInterval: 15_000 });
  return (
    <Card>
      <div className="flex gap-2 border-b p-3">
        <Select className="w-auto" value={status} onChange={(e) => (setStatus(e.target.value), setPage(1))} aria-label="Status">
          <option value="">Alle Ausführungen</option>
          <option value="SUCCESS">Erfolgreich</option>
          <option value="FAILED">Fehlgeschlagen</option>
        </Select>
      </div>
      <Table>
        <THead>
          <TR className="hover:bg-transparent">
            <TH>Zeit</TH>
            <TH>Regel</TH>
            <TH>Ergebnis</TH>
            <TH className="w-24" />
          </TR>
        </THead>
        <TBody>
          {data?.items.length === 0 && <EmptyRow colSpan={4}>Noch keine Ausführungen.</EmptyRow>}
          {data?.items.map((r) => (
            <TR key={r.id}>
              <TD className="whitespace-nowrap text-sm">{formatDateTime(r.createdAt)}</TD>
              <TD>
                <p className="font-medium">{r.rule.name}</p>
                <p className="text-xs text-muted-foreground">{AUTOMATION_TRIGGERS[r.trigger as AutomationTrigger]?.label ?? r.trigger}</p>
              </TD>
              <TD className="text-sm">
                {r.status === 'FAILED' ? <Badge variant="destructive">Fehler</Badge> : <Badge variant="success">Ausgeführt</Badge>}
                <p className="mt-1 text-xs text-muted-foreground">{r.message}</p>
              </TD>
              <TD className="text-right text-sm">
                <Link to={entityLink(r)} className="text-primary hover:underline">
                  Öffnen
                </Link>
              </TD>
            </TR>
          ))}
        </TBody>
      </Table>
      {data && <Pagination page={page} pageSize={25} total={data.total} onPage={setPage} />}
    </Card>
  );
}

export function AutomationsPage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<'rules' | 'runs'>('rules');
  const [edit, setEdit] = useState<{ id: string | null; form: Form } | null>(null);
  const [pick, setPick] = useState(false);
  const [confirmDialog, confirm] = useConfirm();
  const { data, isLoading } = useQuery({ queryKey: KEY, queryFn: () => api<{ items: AutomationRuleItem[] }>('/automations') });
  const { data: meta } = useQuery({ queryKey: ['tickets', 'meta'], queryFn: () => api<TicketMeta>('/tickets/meta') });
  const toggle = useMutation({
    mutationFn: (r: AutomationRuleItem) =>
      api(`/automations/${r.id}`, {
        method: 'PUT',
        body: { name: r.name, description: r.description, active: !r.active, trigger: r.trigger, triggerParams: r.triggerParams, conditions: r.conditions, actions: r.actions, flow: r.flow, sortOrder: r.sortOrder },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
    onError,
  });
  const remove = useMutation({ mutationFn: (id: string) => api(`/automations/${id}`, { method: 'DELETE' }), onSuccess: () => qc.invalidateQueries({ queryKey: KEY }), onError });
  const blank: Form = { name: '', description: '', active: true, trigger: 'ticket.created', triggerParams: {}, conditions: [], actions: [defaultAction('send_email', meta)] };

  return (
    <div className="mx-auto max-w-5xl">
      {confirmDialog}
      <PageHeader
        title="Automatisierungen"
        description="Regeln für Benachrichtigungen, Eskalationen und automatische Schritte – mandantenbezogen und mit Protokoll."
        actions={
          <>
            <Button variant="outline" onClick={() => setPick(true)}>
              <Sparkles /> Aus Vorlage
            </Button>
            <Button onClick={() => setEdit({ id: null, form: blank })}>
              <Plus /> Regel
            </Button>
          </>
        }
      />
      <Tabs
        value={tab}
        onChange={setTab}
        items={[
          { value: 'rules', label: 'Regeln' },
          { value: 'runs', label: <span className="inline-flex items-center gap-1.5"><History className="size-3.5" /> Protokoll</span> },
        ]}
      />
      <div className="mt-4 grid gap-3">
        {tab === 'runs' ? (
          <RunsLog />
        ) : isLoading ? (
          <Skeleton className="h-40" />
        ) : data?.items.length === 0 ? (
          <Card>
            <CardContent className="grid gap-3 py-10 text-center">
              <Zap className="mx-auto size-8 text-muted-foreground" />
              <p className="font-medium">Noch keine Automatisierungen</p>
              <p className="text-sm text-muted-foreground">Starten Sie mit einer Vorlage, z. B. „Ticket 48 Stunden unbearbeitet → Serviceleitung“.</p>
              <div>
                <Button variant="outline" onClick={() => setPick(true)}>
                  <Sparkles /> Vorlage wählen
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          data?.items.map((r) => (
            <Card key={r.id}>
              <CardContent className="flex flex-wrap items-center gap-3 pt-5">
                <Switch checked={r.active} onCheckedChange={() => toggle.mutate(r)} aria-label={`${r.name} ${r.active ? 'deaktivieren' : 'aktivieren'}`} />
                <div className="min-w-0 flex-1">
                  <p className="font-medium">{r.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {AUTOMATION_TRIGGERS[r.trigger as AutomationTrigger]?.label ?? r.trigger}
                    {Object.entries(r.triggerParams).map(([k, v]) => ` (${v} ${k === 'hours' ? 'Std.' : k === 'days' ? 'Tage' : 'Min.'})`)}
                    {r.conditions.length > 0 && ` · ${r.conditions.length} Bedingung${r.conditions.length === 1 ? '' : 'en'}`} →{' '}
                    {r.flow
                      ? (() => {
                          const c = countSteps(r.flow as Step[]);
                          return `Ablauf: ${c.total} Schritte (${c.branches} Verzweigungen, ${c.waits} Wartezeiten)`;
                        })()
                      : r.actions.map((a) => ACTION_DEFS[a.type as AutomationActionType]?.label ?? a.type).join(', ')}
                  </p>
                  <p className="text-xs text-muted-foreground">{r.runCount > 0 ? `${r.runCount}× ausgeführt, zuletzt ${formatRelative(r.lastRunAt!)}` : 'noch nicht ausgeführt'}</p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8"
                  aria-label={`${r.name} bearbeiten`}
                  onClick={() =>
                    setEdit({
                      id: r.id,
                      form: {
                        name: r.name,
                        description: r.description ?? '',
                        active: r.active,
                        trigger: r.trigger as AutomationTrigger,
                        triggerParams: r.triggerParams,
                        conditions: r.conditions as Condition[],
                        actions: r.actions as Action[],
                        flow: (r.flow as Step[] | null) ?? null,
                      },
                    })
                  }
                >
                  <Pencil />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8 text-destructive hover:text-destructive"
                  aria-label={`${r.name} löschen`}
                  onClick={async () => (await confirm({ title: `Regel „${r.name}“ löschen?`, description: 'Das Ausführungsprotokoll der Regel wird mitgelöscht.', confirmLabel: 'Löschen', destructive: true })) && remove.mutate(r.id)}
                >
                  <Trash2 />
                </Button>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <Dialog open={pick} onOpenChange={setPick}>
        <DialogContent wide>
          <DialogHeader>
            <DialogTitle>Vorlage wählen</DialogTitle>
            <DialogDescription>Die Vorlage wird im Editor geöffnet und erst beim Speichern angelegt.</DialogDescription>
          </DialogHeader>
          <DialogBody className="grid gap-2">
            {templates(meta).map((t) => (
              <button
                key={t.title}
                type="button"
                className="rounded-lg border p-3 text-left transition-colors hover:border-primary/50 hover:bg-primary/5"
                onClick={() => {
                  setPick(false);
                  setEdit({ id: null, form: t.form });
                }}
              >
                <span className="block font-medium">{t.title}</span>
                {t.hint && <span className="mt-1 block text-sm text-muted-foreground">{t.hint}</span>}
              </button>
            ))}
            <Alert className="mt-2">Ausgelöst wird jede Regel höchstens einmal je Anlass. Ketten von Regeln, die sich gegenseitig auslösen, werden nach zwei Stufen abgebrochen.</Alert>
          </DialogBody>
        </DialogContent>
      </Dialog>

      <RuleDialog edit={edit} onClose={() => setEdit(null)} />
    </div>
  );
}
